import { apiSlice } from '../api/apiSlice';

const toId = (value) => {
  if (value == null) return null;
  if (typeof value === 'object') {
    const nested = value._id ?? value.id;
    if (nested == null) return null;
    value = nested;
  }
  const text = String(value).trim();
  if (!text || text === 'null' || text === 'undefined' || text === '[object Object]') {
    return null;
  }
  return text;
};

const sameId = (a, b) => {
  const left = toId(a);
  const right = toId(b);
  if (!left && !right) return true;
  return Boolean(left && right && left === right);
};

const rowMatchesParties = (row, arg) =>
  sameId(row?.senderid, arg?.senderid) &&
  sameId(row?.recieverid, arg?.recieverid) &&
  sameId(row?.sendershopid, arg?.sendershopid) &&
  sameId(row?.recievershopid, arg?.recievershopid);

/**
 * Instantly remove matching pending/requested rows from every cached
 * getAllConnected query so the Connection Request inbox updates without
 * waiting for a refetch / page refresh.
 */
const patchConnectionCaches = (dispatch, getState, arg, mode) => {
  const patches = [];
  let entries = [];
  try {
    entries = apiSlice.util.selectInvalidatedBy(getState(), [
      { type: 'UserConnections' },
    ]);
  } catch (_) {
    entries = [];
  }

  entries.forEach((entry) => {
    if (
      entry.endpointName !== 'getAllConnected' &&
      entry.endpointName !== 'getAllConnectedByUserId'
    ) {
      return;
    }
    patches.push(
      dispatch(
        apiSlice.util.updateQueryData(
          entry.endpointName,
          entry.originalArgs,
          (draft) => {
            if (!draft || typeof draft !== 'object') return;

            if (mode === 'accept' || mode === 'reject') {
              if (Array.isArray(draft.pending)) {
                draft.pending = draft.pending.filter(
                  (row) => !rowMatchesParties(row, arg),
                );
              }
            }
            if (mode === 'accept' || mode === 'cancel' || mode === 'reject') {
              if (Array.isArray(draft.requested)) {
                draft.requested = draft.requested.filter(
                  (row) => !rowMatchesParties(row, arg),
                );
              }
            }
            // On accept, also drop from pending/requested and ensure connected grows
            if (mode === 'accept' && Array.isArray(draft.connected)) {
              const already = draft.connected.some((row) =>
                rowMatchesParties(row, arg),
              );
              if (!already) {
                draft.connected.push({
                  ...arg,
                  status: true,
                });
              }
            }
          },
        ),
      ),
    );
  });

  return patches;
};

export const connectionApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /** Get all connected / requested / pending connections for a user/shop */
    getAllConnected: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams(params).toString();
        return `/conection/getAllConnected/?${queryParams}`;
      },
      // Keep each profile scope and page in its own cache entry so switching
      // Messenger tabs can reuse that profile's connections.
      serializeQueryArgs: ({ queryArgs }) => {
        const p = queryArgs && typeof queryArgs === "object" ? queryArgs : {};
        const uid = p.userid || p.userId || "";
        const shop = p.shopid || "";
        const marketer =
          p.isMarketerProfile === true || p.isMarketerProfile === "true"
            ? "marketer"
            : "personal";
        const bt = p.businessType || "all";
        const connectionProfile = p.connectionProfile || "all";
        const page = p.page || "1";
        const limit = p.limit || "all";
        return `getAllConnected(${uid}|${shop}|${marketer}|${bt}|${connectionProfile}|${page}|${limit})`;
      },
      providesTags: ['UserConnections'],
    }),

    /** Get all public/shop entities available to connect with */
    getPublicAgainstShop: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams(params).toString();
        return `/conection/publicOrShopAgainstPublic/?${queryParams}`;
      },
      providesTags: ['Connections'],
    }),

    /** Send a connection request (User A → User B) */
    sendConnectionRequest: builder.mutation({
      query: (body) => ({
        url: '/conection/sendrequestcoonect/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'UserConnections',
        'Connections',
        'Marketers',
        'LevelRanking',
        { type: 'VisitProfile', id: arg.recieverid },
        { type: 'VisitProfile', id: arg.senderid },
      ],
    }),

    /** Accept a pending connection request */
    acceptConnectionRequest: builder.mutation({
      query: (body) => ({
        url: '/conection/acceptrequestcoonect/',
        method: 'POST',
        body,
      }),
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const patches = patchConnectionCaches(
          dispatch,
          getState,
          arg,
          'accept',
        );
        try {
          await queryFulfilled;
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('selectezi:connection_updated', { detail: arg }),
            );
          }
        } catch {
          patches.forEach((p) => p?.undo?.());
        }
      },
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              'UserConnections',
              'Connections',
              'Marketers',
              'LevelRanking',
              { type: 'VisitProfile', id: arg.recieverid },
              { type: 'VisitProfile', id: arg.senderid },
            ],
    }),

    /** Cancel a sent connection request (withdraw before acceptance) */
    cancelConnectionRequest: builder.mutation({
      query: (body) => ({
        url: '/conection/cancelrequestcoonect/',
        method: 'POST',
        body,
      }),
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const patches = patchConnectionCaches(
          dispatch,
          getState,
          arg,
          'cancel',
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo?.());
        }
      },
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              'UserConnections',
              'Connections',
              'Marketers',
              { type: 'VisitProfile', id: arg.recieverid },
              { type: 'VisitProfile', id: arg.senderid },
            ],
    }),

    /** Reject / ignore a received pending request */
    rejectConnectionRequest: builder.mutation({
      query: (body) => ({
        url: '/conection/cancelrequestcoonect/',
        method: 'POST',
        body,
      }),
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const patches = patchConnectionCaches(
          dispatch,
          getState,
          arg,
          'reject',
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo?.());
        }
      },
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              'UserConnections',
              'Connections',
              'Marketers',
              { type: 'VisitProfile', id: arg.recieverid },
              { type: 'VisitProfile', id: arg.senderid },
            ],
    }),

    /** Disconnect from an already connected user/shop */
    disconnect: builder.mutation({
      query: (body) => ({
        url: '/conection/disconnect/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'UserConnections',
        'Connections',
        'Marketers',
        'LevelRanking',
        { type: 'VisitProfile', id: arg.recieverid },
        { type: 'VisitProfile', id: arg.senderid },
      ],
    }),

    /** Get random suggestions to connect with */
    getRandomSuggestions: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams(params).toString();
        return `/conection/getRndmSuggestionToConnect/?${queryParams}`;
      },
      providesTags: ['UserConnections', 'Connections'],
    }),
  }),
});

export const {
  useGetAllConnectedQuery,
  useGetPublicAgainstShopQuery,
  useSendConnectionRequestMutation,
  useAcceptConnectionRequestMutation,
  useCancelConnectionRequestMutation,
  useRejectConnectionRequestMutation,
  useDisconnectMutation,
  useGetRandomSuggestionsQuery,
} = connectionApiSlice;
